import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Info, FileSpreadsheet, FileCode } from "lucide-react";
import api from "@/lib/api";
import { eur } from "@/lib/format";

const CURRENT_YEAR = new Date().getFullYear();

export function VatQuarterly({ initialYear = CURRENT_YEAR, showYearSelector = true }) {
  const [yr, setYr] = useState(Number(initialYear) || CURRENT_YEAR);
  const [dl, setDl] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["vat-quarterly", yr],
    queryFn: async () => (await api.get("/vat/quarterly", { params: { year: yr } })).data,
  });

  const years = [];
  for (let y = CURRENT_YEAR; y >= CURRENT_YEAR - 4; y--) years.push(y);

  const quarters = data?.quarters || [];

  const download = async (quarter, format) => {
    const key = `${quarter}-${format}`;
    setDl(key);
    try {
      const { data: blob } = await api.get("/vat/quarterly/export", {
        params: { year: yr, quarter, format },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `UStVA-${yr}-Q${quarter}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export fehlgeschlagen");
    } finally {
      setDl("");
    }
  };

  return (
    <div data-testid="vat-quarterly">
      {showYearSelector && (
        <div className="flex items-center justify-end mb-4">
          <select
            value={yr}
            onChange={(e) => setYr(Number(e.target.value))}
            data-testid="vat-year-select"
            className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      )}

      {isLoading ? (
        <div className="py-10 text-center text-sm text-slate-400">Lädt…</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vat-table">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-slate-200">
                  <th className="py-2 pr-3 font-medium">Quartal</th>
                  <th className="py-2 px-3 font-medium text-right">Netto 19% <span className="text-slate-300">(Kz 81)</span></th>
                  <th className="py-2 px-3 font-medium text-right">Netto 7% <span className="text-slate-300">(Kz 86)</span></th>
                  <th className="py-2 px-3 font-medium text-right">USt gesamt <span className="text-slate-300">(Kz 83)</span></th>
                  <th className="py-2 px-3 font-medium text-right">Rechn.</th>
                  <th className="py-2 pl-3 font-medium text-right">Datei</th>
                </tr>
              </thead>
              <tbody>
                {quarters.map((q) => (
                  <tr key={q.quarter} className="border-b border-slate-100" data-testid={`vat-q${q.quarter}`}>
                    <td className="py-2.5 pr-3">
                      <span className="font-semibold text-slate-800">{q.label}</span>
                      <span className="text-slate-400 text-xs ml-1">{q.months}</span>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono tabular-nums">{eur(q.net_19)}</td>
                    <td className="py-2.5 px-3 text-right font-mono tabular-nums">{eur(q.net_7)}</td>
                    <td className="py-2.5 px-3 text-right font-mono tabular-nums font-semibold text-slate-900">{eur(q.total_vat)}</td>
                    <td className="py-2.5 px-3 text-right text-slate-500">{q.count}</td>
                    <td className="py-2.5 pl-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => download(q.quarter, "csv")}
                          disabled={dl === `${q.quarter}-csv`}
                          title={`Q${q.quarter} als CSV`}
                          data-testid={`vat-export-csv-q${q.quarter}`}
                          className="h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                        >
                          <FileSpreadsheet className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => download(q.quarter, "xml")}
                          disabled={dl === `${q.quarter}-xml`}
                          title={`Q${q.quarter} als XML`}
                          data-testid={`vat-export-xml-q${q.quarter}`}
                          className="h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                        >
                          <FileCode className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="py-2.5 pr-3">Jahr {data?.year}</td>
                  <td className="py-2.5 px-3"></td>
                  <td className="py-2.5 px-3"></td>
                  <td className="py-2.5 px-3 text-right font-mono tabular-nums text-blue-700" data-testid="vat-year-total">{eur(data?.total_vat_year)}</td>
                  <td className="py-2.5 px-3"></td>
                  <td className="py-2.5 pl-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />
            <span>
              Basis: <strong>Ist-Versteuerung</strong> (nach Zahlungseingang, nur bezahlte Rechnungen). Ohne Vorsteuer.
              Die Kennzahlen (Kz&nbsp;81/86 = Bemessungsgrundlage ohne Cent, Kz&nbsp;83 = verbleibende USt-Vorauszahlung) übertragen Sie in „Mein&nbsp;ELSTER". Keine steuerliche Beratung.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
